import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  clickRadioButton,
  getNotificationSection,
  getSelectedRadioText,
  getToggleOn,
  setToggle,
} from './fbNotificationUtils'
import { notificationsUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import SettingNotFoundException from '../exception/settingNotFoundException'
import * as promises from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Setting property functions
const getEmailFrequencyCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value || null)
const getTogglesCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) =>
    value && value.length > 0 ? value.join(', ') : 'Off',
  )

const currentValueFns = {
  [FacebookSettingKey.REMINDER_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.REMINDER_NOTIFICATIONS,
  ),
  [FacebookSettingKey.FRIENDS_UPDATES]: getTogglesCurrentValueFn(
    FacebookSettingKey.FRIENDS_UPDATES,
  ),
  [FacebookSettingKey.PEOPLE_YOU_MAY_KNOW]: getTogglesCurrentValueFn(
    FacebookSettingKey.PEOPLE_YOU_MAY_KNOW,
  ),
  [FacebookSettingKey.BIRTHDAY_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.BIRTHDAY_NOTIFICATIONS,
  ),
  [FacebookSettingKey.GROUP_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.GROUP_NOTIFICATIONS,
  ),
  [FacebookSettingKey.EVENTS_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.EVENTS_NOTIFICATIONS,
  ),
  [FacebookSettingKey.FOLLOWED_PAGES_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.FOLLOWED_PAGES_NOTIFICATIONS,
  ),
  [FacebookSettingKey.OTHER_NOTIFICATIONS]: getTogglesCurrentValueFn(
    FacebookSettingKey.OTHER_NOTIFICATIONS,
  ),
  [FacebookSettingKey.BROWSER_SOUNDS]: getTogglesCurrentValueFn(
    FacebookSettingKey.BROWSER_SOUNDS,
  ),
  [FacebookSettingKey.EMAIL_FREQUENCY]: getEmailFrequencyCurrentValueFn(
    FacebookSettingKey.EMAIL_FREQUENCY,
  ),
}
const getCurrentValueFn = (key) => currentValueFns[key]

const EMAIL_FREQUENCY_VALUES = {
  ALL: 'All',
  SUGGESTED: 'Suggested',
  REQUIRED: 'Required notifications',
}

const EMAIL_FREQUENCY_SETTING_LEVEL = {
  [EMAIL_FREQUENCY_VALUES.ALL]: 0,
  [EMAIL_FREQUENCY_VALUES.SUGGESTED]: 1,
  [EMAIL_FREQUENCY_VALUES.REQUIRED]: 2,
}

const getEmailFrequencyIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(
    key,
    (value) =>
      EMAIL_FREQUENCY_SETTING_LEVEL[value] &&
      EMAIL_FREQUENCY_SETTING_LEVEL[value] ===
        EMAIL_FREQUENCY_SETTING_LEVEL[EMAIL_FREQUENCY_VALUES.REQUIRED],
  )

const getTogglesIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => (value ? value.length === 0 : true))

const isFulfilledFns = {
  [FacebookSettingKey.REMINDER_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.REMINDER_NOTIFICATIONS,
  ),
  [FacebookSettingKey.FRIENDS_UPDATES]: getTogglesIsFulfilledFn(
    FacebookSettingKey.FRIENDS_UPDATES,
  ),
  [FacebookSettingKey.PEOPLE_YOU_MAY_KNOW]: getTogglesIsFulfilledFn(
    FacebookSettingKey.PEOPLE_YOU_MAY_KNOW,
  ),
  [FacebookSettingKey.BIRTHDAY_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.BIRTHDAY_NOTIFICATIONS,
  ),
  [FacebookSettingKey.GROUP_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.GROUP_NOTIFICATIONS,
  ),
  [FacebookSettingKey.EVENTS_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.EVENTS_NOTIFICATIONS,
  ),
  [FacebookSettingKey.FOLLOWED_PAGES_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.FOLLOWED_PAGES_NOTIFICATIONS,
  ),
  [FacebookSettingKey.OTHER_NOTIFICATIONS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.OTHER_NOTIFICATIONS,
  ),
  [FacebookSettingKey.BROWSER_SOUNDS]: getTogglesIsFulfilledFn(
    FacebookSettingKey.BROWSER_SOUNDS,
  ),
  [FacebookSettingKey.EMAIL_FREQUENCY]: getEmailFrequencyIsFulfilledFn(
    FacebookSettingKey.EMAIL_FREQUENCY,
  ),
}
const getIsFulfilledFn = (key) => isFulfilledFns[key]

const getCantUndoFn = () => defaultCantUndoFn
function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

const notificationToggleRowLabels: Partial<Record<FacebookSettingKey, string>> =
  {
    [FacebookSettingKey.REMINDER_NOTIFICATIONS]: 'Reminders',
    [FacebookSettingKey.FRIENDS_UPDATES]: 'Updates from friends',
    [FacebookSettingKey.PEOPLE_YOU_MAY_KNOW]: 'People you may know',
    [FacebookSettingKey.BIRTHDAY_NOTIFICATIONS]: 'Birthdays',
    [FacebookSettingKey.GROUP_NOTIFICATIONS]: 'Groups',
    [FacebookSettingKey.EVENTS_NOTIFICATIONS]: 'Events',
    [FacebookSettingKey.FOLLOWED_PAGES_NOTIFICATIONS]: 'Pages You Follow',
    [FacebookSettingKey.OTHER_NOTIFICATIONS]: 'Other Notifications',
    [FacebookSettingKey.BROWSER_SOUNDS]: 'Browser',
  }

const notificationToggleSettingLabels: Partial<
  Record<FacebookSettingKey, string[]>
> = {
  [FacebookSettingKey.REMINDER_NOTIFICATIONS]: [
    'Allow notifications on Facebook',
  ],
  [FacebookSettingKey.FRIENDS_UPDATES]: ['Allow notifications on Facebook'],
  [FacebookSettingKey.PEOPLE_YOU_MAY_KNOW]: ['Allow notifications on Facebook'],
  [FacebookSettingKey.BIRTHDAY_NOTIFICATIONS]: [
    'Allow notifications on Facebook',
  ],
  [FacebookSettingKey.GROUP_NOTIFICATIONS]: ['Email', 'SMS'],
  [FacebookSettingKey.EVENTS_NOTIFICATIONS]: [
    'Allow notifications on Facebook',
  ],
  [FacebookSettingKey.FOLLOWED_PAGES_NOTIFICATIONS]: [
    'Allow notifications on Facebook',
  ],
  [FacebookSettingKey.OTHER_NOTIFICATIONS]: ['Allow notifications on Facebook'],
  [FacebookSettingKey.BROWSER_SOUNDS]: [
    'Play a sound when each new notification is received',
    'Play a sound when a message is received',
  ],
}

function getEmailFrequencyValue(label: string): string {
  if (label.includes(EMAIL_FREQUENCY_VALUES.ALL)) {
    return EMAIL_FREQUENCY_VALUES.ALL
  } else if (label.includes(EMAIL_FREQUENCY_VALUES.SUGGESTED)) {
    return EMAIL_FREQUENCY_VALUES.SUGGESTED
  } else if (label.includes(EMAIL_FREQUENCY_VALUES.REQUIRED)) {
    return EMAIL_FREQUENCY_VALUES.REQUIRED
  }
  return ''
}

// Gather stage

enum StageKey {
  GATHER_SETTING_ENGAGEMENT_NOTIFICATIONS = 'GATHER_SETTING_ENGAGEMENT_NOTIFICATIONS',
  GATHER_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY = 'GATHER_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY',
  UPDATE_SETTING_ENGAGEMENT_NOTIFICATIONS = 'UPDATE_SETTING_ENGAGEMENT_NOTIFICATIONS',
  UPDATE_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY = 'UPDATE_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY',
  UNDO_SETTING_ENGAGEMENT_NOTIFICATIONS = 'UNDO_SETTING_ENGAGEMENT_NOTIFICATIONS',
  UNDO_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY = 'UNDO_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_ENGAGEMENT_NOTIFICATIONS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      const automationErrors = {}

      for (const settingKey of Object.keys(notificationToggleRowLabels)) {
        try {
          const section = await getNotificationSection(
            notificationToggleRowLabels[settingKey],
          )
          if (section && section.ariaExpanded !== 'true') {
            section.click()
            await promises.wait(500)
          }

          const initialValuesOn = []
          for (const settingLabel of notificationToggleSettingLabels[
            settingKey
          ]) {
            const toggleValue = await getToggleOn(
              notificationToggleRowLabels[settingKey],
              settingLabel,
            )
            if (toggleValue) {
              initialValuesOn.push(settingLabel)
            }
          }

          await updateStoredInitialValues(
            {
              [settingKey]: initialValuesOn,
            },
            FBRecommendationKey.FB_ENGAGEMENT_NOTIFICATIONS,
          )
        } catch (error) {
          automationErrors[settingKey] = {
            gatherError: { message: error.message, name: error.name },
          }

          await updateStoredAutomationErrors(
            automationErrors,
            FBRecommendationKey.FB_ENGAGEMENT_NOTIFICATIONS,
          )
        }
      }
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      const automationErrors = {}
      try {
        let section = await getNotificationSection('Email')
        if (section && section.ariaExpanded !== 'true') {
          section.click()
          await promises.wait(500)
          section = await getNotificationSection('Email')
        }

        if (section.ariaExpanded !== 'true') {
          const error = new SettingNotFoundException(
            `No email configured for account.`,
          )
          automationErrors[FacebookSettingKey.EMAIL_FREQUENCY] = {
            gatherError: { message: error.message, name: error.name },
          }

          await updateStoredAutomationErrors(
            automationErrors,
            FBRecommendationKey.FB_ENGAGEMENT_NOTIFICATIONS,
          )
        } else {
          const radioLabel = await getSelectedRadioText(
            'Email',
            // Make sure not to get radio button for selecting primary email
            (e: HTMLElement) =>
              e.textContent.toLowerCase().includes('notifications'),
          )
          await updateStoredInitialValues(
            {
              [FacebookSettingKey.EMAIL_FREQUENCY]:
                getEmailFrequencyValue(radioLabel),
            },
            FBRecommendationKey.FB_ENGAGEMENT_NOTIFICATIONS,
          )
        }
      } catch (error) {
        automationErrors[FacebookSettingKey.EMAIL_FREQUENCY] = {
          gatherError: { message: error.message, name: error.name },
        }
        await updateStoredAutomationErrors(
          automationErrors,
          FBRecommendationKey.FB_ENGAGEMENT_NOTIFICATIONS,
        )
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ENGAGEMENT_NOTIFICATIONS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      for (const settingKey of Object.keys(notificationToggleRowLabels)) {
        if (
          shouldUpdateSetting(
            settingsSelected,
            initialValues,
            settingKey,
            getIsFulfilledFn,
          ) &&
          !isDry
        ) {
          const section = await getNotificationSection(
            notificationToggleRowLabels[settingKey],
          )
          if (section && section.ariaExpanded !== 'true') {
            section.click()
            await promises.wait(500)
          }

          for (const settingLabel of notificationToggleSettingLabels[
            settingKey
          ]) {
            await setToggle(
              notificationToggleRowLabels[settingKey],
              settingLabel,
              false,
            )
            await promises.wait(500)
          }
        }
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.EMAIL_FREQUENCY,
          getIsFulfilledFn,
        )
      ) {
        return null
      }

      const section = await getNotificationSection('Email')
      if (section && section.ariaExpanded !== 'true') {
        section.click()
        await promises.wait(500)
      }

      if (!isDry) {
        await clickRadioButton('Email', EMAIL_FREQUENCY_VALUES.REQUIRED)
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_ENGAGEMENT_NOTIFICATIONS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected || !initialValues) {
        return null
      }

      for (const settingKey of Object.keys(notificationToggleRowLabels)) {
        if (settingsSelected[settingKey] && !isDry) {
          const section = await getNotificationSection(
            notificationToggleRowLabels[settingKey],
          )
          if (section && section.ariaExpanded !== 'true') {
            section.click()
            await promises.wait(500)
          }

          for (const settingLabel of initialValues[settingKey]) {
            await setToggle(
              notificationToggleRowLabels[settingKey],
              settingLabel,
              true,
            )
            await promises.wait(500)
          }
        }
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_NOTIFICATIONS_EMAIL_FREQUENCY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (
        !settingsSelected ||
        !settingsSelected[FacebookSettingKey.EMAIL_FREQUENCY] ||
        !initialValues[FacebookSettingKey.EMAIL_FREQUENCY]
      ) {
        return null
      }

      const section = await getNotificationSection('Email')
      if (section && section.ariaExpanded !== 'true') {
        section.click()
        await promises.wait(500)
      }

      if (!isDry) {
        await clickRadioButton(
          'Email',
          initialValues[FacebookSettingKey.EMAIL_FREQUENCY],
        )
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
